
// "First, let's confirm your details" — step 1 of the AEP renewal wizard
// (#656, reference screenshot 2).
//
// County and state are DERIVED from the ZIP rather than entered. The reference
// design shows them as filled, read-only confirmation fields, and that is also
// the correct behaviour: `fips` is what scopes the plan catalog, the provider
// search and the pharmacy search, so it has to come from the ZIP lookup. The
// one case that needs a control is a ZIP spanning multiple counties, where a
// select appears — otherwise the single result is applied silently.
import AddressSearch from '~/components/AddressSearch.vue'
import TypedDateInput from '~/components/TypedDateInput.vue'

export default {
    components: { AddressSearch, TypedDateInput },
    props: {
        target: { type: Object, required: true },
    },
    data () {
        return {
            counties: [],
            resolving: false,
        }
    },
    computed: {
        // An unborn client is a typo, not a birth date.
        maxDob () { return new Date().toISOString().split('T')[0] },
        countyLabel () {
            if (this.resolving) { return 'Looking up your county…' }
            if (this.county) { return this.county }
            return this.counties.length > 1 ? 'Choose your county below' : '—'
        },
        county () {
            const match = this.counties.find(c => c.fips === this.target.fips)
            return match ? this.countyName(match) : null
        },
    },
    created () {
        // Hydrated survey may already carry a ZIP; resolve so the county line
        // shows a name rather than a dash on first paint.
        if (this.target.zip_code) { this.loadCounties(this.target.zip_code) }
    },
    methods: {
        patch (deltas) {
            Object.entries(deltas).forEach(([k, v]) => { this.$set(this.target, k, v === '' ? null : v) })
        },
        onAddressPick (a) {
            this.patch({ street_1: a.street_1 || '', street_2: a.street_2 || '', city: a.city || '' })
            this.onZip(a.zip_code || '')
        },
        countyName (c) {
            return [c.county || c.name, c.state].filter(Boolean).join(', ')
        },
        onZip (raw) {
            const zip = (raw || '').replace(/\D/g, '').slice(0, 5)
            // A partial ZIP invalidates the derived pair — leaving a stale
            // county/fips attached to a half-typed ZIP would silently scope
            // every later search to the wrong place.
            this.patch({ zip_code: zip, fips: null, state: null })
            this.counties = []
            if (zip.length === 5) { this.loadCounties(zip) }
        },
        // Verified against /api/proxies/oracle/counties_for_zip/01844/, which
        // answers { counties: [{ fips, name, state }] } — no `county` key,
        // despite what WelcomeStep.loadCounty's comment says. countyName()
        // reads `county || name` for that reason. The display therefore reads
        // "Essex, MA" rather than the platform's "Essex County, MA": appending
        // the suffix would need a per-state rule (Louisiana has parishes,
        // Alaska boroughs), and this matches what WelcomeStep already renders.
        async loadCounties (zip) {
            this.resolving = true
            const resp = await this.$api.getCountiesForZip(zip).catch(() => null)
            this.counties = (resp && resp.counties) || []
            this.resolving = false
            if (this.counties.length === 1) {
                this.pickCounty(this.counties[0].fips)
            }
        },
        pickCounty (fips) {
            const c = this.counties.find(x => x.fips === fips)
            this.patch({ fips: fips || null, state: (c && c.state) || null })
        },
    },
}
